import type { TokenStorage, Tokens } from "../types";

const DEFAULT_KEY = "ferrumec.auth.tokens";

function safeParse(raw: string | null): Tokens | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    return null;
  }
}

/** Persists tokens in `window.localStorage`. Survives browser restarts — used for "remember me". */
export class LocalStorageTokenStorage implements TokenStorage {
  constructor(private readonly key: string = DEFAULT_KEY) {}

  load(): Tokens | null {
    if (typeof window === "undefined") return null;
    return safeParse(window.localStorage.getItem(this.key));
  }

  save(tokens: Tokens): void {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(this.key, JSON.stringify(tokens));
  }

  clear(): void {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(this.key);
  }
}

/** Persists tokens in `window.sessionStorage`. Cleared when the tab closes. */
export class SessionStorageTokenStorage implements TokenStorage {
  constructor(private readonly key: string = DEFAULT_KEY) {}

  load(): Tokens | null {
    if (typeof window === "undefined") return null;
    return safeParse(window.sessionStorage.getItem(this.key));
  }

  save(tokens: Tokens): void {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(this.key, JSON.stringify(tokens));
  }

  clear(): void {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(this.key);
  }
}

/** In-memory only. Nothing persists across reload — safest option for shared/kiosk machines. */
export class MemoryTokenStorage implements TokenStorage {
  private tokens: Tokens | null = null;

  load(): Tokens | null {
    return this.tokens;
  }

  save(tokens: Tokens): void {
    this.tokens = tokens;
  }

  clear(): void {
    this.tokens = null;
  }
}
